import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import FinalCTA from "@/components/sections/FinalCTA";
import { Stagger, StaggerItem } from "@/components/motion";
import { SectionHeading } from "@/components/primitives";
import Icon from "@/components/Icon";

/**
 * Features page — comprehensive Stage 1 capability reference.
 * Accurate to what actually ships. "Coming Soon" clearly marked.
 */

const GROUPS = [
  {
    id: "command-center",
    icon: "LayoutDashboard",
    name: "Command Center",
    tagline: "Run the day.",
    items: [
      "Business Briefing — daily compact operational summary",
      "Today's priorities from your real workspace state",
      "Overdue tasks + upcoming deadlines",
      "Deals needing follow-up",
      "Upcoming commitments and meetings",
      "Primary goal + next milestone",
      "Pipeline pulse and business health",
      "Recent activity across every module",
    ],
  },
  {
    id: "board",
    icon: "Landmark",
    name: "Board · Leadership Team",
    tagline: "Think about the business.",
    items: [
      "CEO · Strategy",
      "CGO · Growth",
      "CMO · Marketing",
      "CFO · Finance",
      "COO · Operations",
      "CTO · Technology",
      "CHRO · People",
      "Chief of Staff — drafts execution plans from adopted decisions",
      "Ask the Board — follow-up strategic questions grounded in your data",
      "Business-context learning as your workspace grows",
      "Decision adoption workflow",
      "Decision → execution plan handoff",
    ],
  },
  {
    id: "goals",
    icon: "Target",
    name: "Goals",
    tagline: "Direction, made measurable.",
    items: [
      "Strategic goals with roadmaps drafted from business context",
      "Supporting goals",
      "Milestones with progress tracking",
      "Category, priority, target, target date, target unit",
      "Connected to Work, Projects and Command Center",
    ],
  },
  {
    id: "work",
    icon: "CheckSquare",
    name: "Work",
    tagline: "The tasks that move things forward.",
    items: [
      "Tasks with title, description, priority, status",
      "Start date/time and due date/time",
      "Reminders and workspace-local scheduling",
      "Recurrence patterns",
      "Assignee (multi-member)",
      "Related company / contact / deal / project / goal",
      "Push + email reminders",
    ],
  },
  {
    id: "sales",
    icon: "Contact",
    name: "Sales / CRM",
    tagline: "Keep revenue moving.",
    items: [
      "Companies (18+ fields)",
      "Contacts (20+ fields, universal name handling)",
      "Deals with stages: New → Qualified → Proposal → Negotiation → Won / Lost",
      "Pipeline board with drag-to-stage",
      "Follow-ups with due-flags",
      "Multi-currency deal value",
      "Won-deal → next-step plan (delivery / onboarding / follow-up)",
      "CRM CSV/XLSX import + export",
      "Relational import (Company + Contact + Deal from one row)",
    ],
  },
  {
    id: "projects",
    icon: "FolderKanban",
    name: "Projects",
    tagline: "Deliver, milestone by milestone.",
    items: [
      "Projects with deadline, owner and linked deal/company",
      "Milestones with status and progress",
      "Connected tasks that roll up progress automatically",
      "Draft plan on project creation (review + approve)",
    ],
  },
  {
    id: "calendar",
    icon: "Calendar",
    name: "Calendar",
    tagline: "One view of your commitments.",
    items: [
      "Tasks with dates surfaced on the calendar",
      "Goal deadlines and project milestones visible",
      "Workspace-local timezone handling",
    ],
  },
  {
    id: "business-data",
    icon: "Database",
    name: "Business Data",
    tagline: "Track what makes your business unique.",
    items: [
      "Custom trackers for records that don't belong in CRM/Projects",
      "Field types: Text, Long text, Number, Date, Checkbox, Single Select, URL, Email",
      "Tracker Builder — describe what you want to track, review the schema, approve",
      "Full CRUD on records with type validation",
      "Guardrails redirect to CRM/Projects/Work when it belongs there",
      "Growth plan and above",
    ],
  },
  {
    id: "meeting-assist",
    icon: "NotebookPen",
    name: "Meeting Assist",
    tagline: "Turn meetings into action.",
    items: [
      "Capture meeting notes and outcomes",
      "Link to related company / contact / deal",
      "Attach action items to Work",
    ],
  },
  {
    id: "assistant",
    icon: "MessageSquare",
    name: "Business Assistant",
    tagline: "Don't just ask. Delegate.",
    items: [
      "Ask questions grounded in your workspace",
      "Create tasks, contacts, companies and deals from natural language",
      "Retrieve and analyse workspace information",
      "Give context and outcomes — not just commands",
      "Draft communications and follow-ups",
    ],
  },
];

const COMING_SOON = [
  { icon: "FileText", name: "Documents", desc: "Living documents with connected business context." },
  { icon: "BarChart3", name: "Reports", desc: "Turn operational data into decision-ready reports." },
  { icon: "Plug", name: "Integrations", desc: "Connect the tools you already use — expanding roadmap." },
];

export default function Features() {
  return (
    <>
      <Seo
        title="Features — What you can do in WorkSpace"
        description="Comprehensive capability reference for Stupeco WorkSpace — Command Center, Board, Goals, Work, Sales/CRM, Projects, Business Data, Meeting Assist, Business Assistant."
        path="/features"
      />
      <PageHeader
        eyebrow="Features"
        title="Everything you can do in WorkSpace today."
        subtitle="A complete reference of Stage 1 capabilities — what's live, and what's clearly marked as Coming Soon."
      />

      {/* Live capability groups */}
      <section className="border-b border-border py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Stagger className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {GROUPS.map((g) => (
              <StaggerItem key={g.id} className="h-full">
                <div id={g.id} className="card-hover flex h-full flex-col gap-4 rounded-2xl border border-border bg-card p-6 hover:border-foreground/20" data-testid={`feature-${g.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-muted/60 text-brand-teal">
                      <Icon name={g.icon} className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="font-display text-base font-semibold tracking-tight">{g.name}</h3>
                      <p className="text-[11px] text-muted-foreground">{g.tagline}</p>
                    </div>
                  </div>
                  <ul className="space-y-1.5 border-t border-border pt-4">
                    {g.items.map((it) => (
                      <li key={it} className="flex items-start gap-2 text-[13px] leading-relaxed text-muted-foreground">
                        <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-brand-teal" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Coming Soon */}
      <section className="border-b border-border py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            eyebrow="Coming Soon"
            title="Being built next."
            subtitle="Honest about what's live vs. what's on the way."
          />
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {COMING_SOON.map((c) => (
              <div key={c.name} className="rounded-2xl border border-dashed border-border bg-card/40 p-6" data-testid={`coming-${c.name.toLowerCase()}`}>
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-muted/40 text-muted-foreground">
                    <Icon name={c.icon} className="h-4 w-4" />
                  </span>
                  <h3 className="font-display text-base font-semibold tracking-tight text-muted-foreground">{c.name}</h3>
                  <span className="ml-auto rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Soon</span>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
